import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../App.css";
import "../../profile.css";
import "../../common.css";
import { ArrowRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import service from "../../services/service";
import endpoints from "../../services/endpoints";

const TeamManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [team, setTeam] = useState([]);
  const [challengeTeams, setChallengeTeams] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loadingChallengeTeams, setLoadingChallengeTeams] = useState(false);

  /* MY TEAMS */
  const fetchTeamlist = useCallback(async () => {
    try {
      const res = await service.get(
        endpoints.teammanagment.teamlist,
        {},
        { skipToast: true },
      );

      const teamData = res?.data?.data?.data || [];

      setTeam(teamData);
    } catch (error) {
      console.log(error);
      setTeam([]);
    }
  }, []);

  /* CHALLENGE TEAMS */
  const fetchChallengeTeams = useCallback(async () => {
    if (!id) {
      setChallengeTeams([]);
      return;
    }

    try {
      setLoadingChallengeTeams(true);

      const res = await service.get(
        endpoints.teammanagment.challengeTeams(id),
        {
          page: 1,
          limit: 10,
        },
        { skipToast: true },
      );

      setChallengeTeams(res?.data?.data?.teams || []);
    } catch (error) {
      console.log(error);
      setChallengeTeams([]);
    } finally {
      setLoadingChallengeTeams(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTeamlist();
  }, [fetchTeamlist]);

  useEffect(() => {
    if (activeTab === "all") {
      fetchChallengeTeams();
    }
  }, [activeTab, fetchChallengeTeams]);

  const createdTeams = useMemo(() => {
    return team.filter((item) => item?.teamRole === "CAPTAIN");
  }, [team]);

  const joinedTeams = useMemo(() => {
    return team.filter((item) => item?.teamRole !== "CAPTAIN");
  }, [team]);

  const handledetail = (item) => {
    const teamId = item?.teamId || item?.id;

    if (!teamId) return;

    // Pass the active tab state explicitly so TeamDetails gets the right flags
    navigate(`/team-management/${teamId}`, {
      state: {
        isFromAllTeams: activeTab === "all",
        isFromCreatedTeams: activeTab === "created",
        isFromJoinedTeams: activeTab === "joined",
      },
    });
  };

  const renderTeamCard = (item, index) => (
    <div
      key={item?.teamId || item?.id || index}
      className="team-card"
      onClick={() => handledetail(item)}
    >
      <div className="team-card-left">
        <div className="team-logo">
          <img src="/challenge_icon.svg" alt="strava" />
        </div>

        <div>
          <h3>{item?.teamName}</h3>
          <p>{item?.memberCount || 0} Members</p>
        </div>
      </div>

      <ArrowRight size={20} />
    </div>
  );

  return (
    <div className="team-page">
      <div className="team-top">
        <h2>Team Management</h2>
      </div>

      <div className="team-actions">
        <div className="team-tabs">
          <button
            className={`team-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All Teams
          </button>

          <button
            className={`team-tab ${activeTab === "created" ? "active" : ""}`}
            onClick={() => setActiveTab("created")}
          >
            Teams Created By Me
          </button>

          <button
            className={`team-tab ${activeTab === "joined" ? "active" : ""}`}
            onClick={() => setActiveTab("joined")}
          >
            Teams Joined By Me
          </button>
        </div>

        <button
          className="red-btn"
          onClick={() => navigate("/team-management/create-group")}
        >
          Create Group
        </button>
      </div>

      <div className="team-grid">
        {/* ALL TEAMS */}
        {activeTab === "all" && (
          <>
            {!id ? (
              <div className="empty-team-state">No Challenge Selected</div>
            ) : loadingChallengeTeams ? (
              <div className="empty-team-state">Loading teams...</div>
            ) : challengeTeams.length > 0 ? (
              challengeTeams.map(renderTeamCard)
            ) : (
              <div className="empty-team-state">
                No teams found for this challenge
              </div>
            )}
          </>
        )}

        {/* CREATED BY ME */}
        {activeTab === "created" && (
          <>
            {createdTeams.length > 0 ? (
              createdTeams.map(renderTeamCard)
            ) : (
              <div className="empty-team-state">No created teams found</div>
            )}
          </>
        )}

        {/* JOINED BY ME */}
        {activeTab === "joined" && (
          <>
            {joinedTeams.length > 0 ? (
              joinedTeams.map(renderTeamCard)
            ) : (
              <div className="empty-team-state">No joined teams found</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
